import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  StandardizedTable,
  type TableColumn,
  type TableRow,
} from "@/components/ui/standardized-table";
import GlobalFilter from "@/components/common/GlobalFilter";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { useAccount } from "@/contexts/account.context";
import { Skeleton } from "@/components/ui/skeleton";
import {
  formatCurrency,
  formatInteger,
  formatPercent,
  formatSignedPercent,
} from "@/utils/numberFormat";
import { apiV1SalesFunnelStage1BreakdownGet } from "@/backend-sdk/client/sdk.gen";
import type {
  SalesFunnelStage1Block as GeneratedBlock,
  SalesFunnelStage1Row as GeneratedRow,
} from "@/backend-sdk/client/types.gen";

/**
 * DETALHAMENTO ETAPA 1 — a página que o item 31 da auditoria da Magnata registrou como
 * ausente.
 *
 * A §7 da auditoria manda conferir "página ausente" em três lugares antes de virar
 * trabalho — rota, tela e menu —, porque no item 25 só faltava o terceiro. Aqui faltavam os
 * três, e um quarto: o **conceito**. Topo/Meio/Fundo de funil não existia em linha nenhuma
 * do painel nem do legado, e `objective` não era pedido ao Graph em rota nenhuma.
 *
 * ## O que ela responde
 *
 * A etapa 1 do card FUNIL DE VENDAS, na Visão Geral, mostra o investimento de mídia paga do
 * período num número só. Esta página o abre em três faixas por plataforma, que é a pergunta
 * que o card não responde: **quanto do orçamento está comprando descoberta, quanto está
 * comprando consideração e quanto está comprando venda**.
 *
 * ## Os números são os mesmos do card
 *
 * `cpc`, `ctr`, `cpm` e `connect_rate` saem das mesmas fórmulas, e o rodapé do bloco do Meta
 * reproduz a etapa 1 azul: medido em 2026-09-05, na janela do relatório da Magnata, o total
 * dá investimento R$ 7.315,55, CPC R$ 0,88 e CPM R$ 35,72 — os três números que a auditoria
 * registra como os do Looker no item 23, alcançados aqui por outro caminho (grão de
 * campanha, e não de conta).
 *
 * ## O rodapé vem da rota, e some quando ela não o manda
 *
 * ⚠️ **`total` é passado, e nunca montado aqui.** Sem rodapé pronto, o `StandardizedTable`
 * soma toda coluna numérica e tira a média das percentuais — e três CPCs somados não dão CPC
 * nenhum. É o padrão A que a auditoria encontrou em sete tabelas.
 *
 * ⚠️ E quando `total_row` vem **nulo**, a tabela recebe `hideTotals`. O invariante da rota é
 * "nulo ⟺ o bloco não tem campanha nenhuma"; passar `undefined` ali devolveria o rodapé
 * automático justamente onde o servidor decidiu não publicar nenhum — hoje ele sairia zerado
 * por coincidência, porque bloco sem campanha é bloco zerado, e amanhã não.
 *
 * ## Os três estados de carga são distintos, e nenhum mente sobre o outro
 *
 * ⚠️ **`combined` não é anulável no contrato**, e o service sempre o constrói: um cliente sem
 * conta de mídia paga recebe os três blocos zerados, e não uma resposta vazia. Então "vazio"
 * e "falhou" são coisas diferentes — a página as separa, porque desenhar "sem conta
 * conectada" depois de um `500` seria uma afirmação sobre os dados no lugar de um erro.
 */

type LinhaDaEtapa = GeneratedRow & TableRow;

/** `+12,34%` / `-12,34%` — com o sinal explícito, e não só a cor (item 34 da auditoria). */
const variacao = (valor: unknown): string => {
  const numero = Number(valor ?? 0);
  if (!Number.isFinite(numero) || numero === 0) return "—";
  return formatSignedPercent(numero);
};

const COLUNAS_COMUNS: TableColumn[] = [
  { key: "stage_label", header: "Etapa", align: "left", type: "text" },
  {
    key: "campaigns",
    header: "Campanhas",
    align: "right",
    type: "number",
    render: (value) => formatInteger(Number(value ?? 0)),
  },
  {
    key: "investment",
    header: "Investimento",
    align: "right",
    type: "currency",
    render: (value) => formatCurrency(Number(value ?? 0)),
  },
  {
    key: "investment_change_percentage",
    header: "Variação",
    align: "right",
    type: "percent",
    render: variacao,
  },
  {
    key: "investment_share",
    header: "Participação",
    align: "right",
    type: "percent",
    render: (value) => formatPercent(Number(value ?? 0)),
  },
  {
    key: "impressions",
    header: "Impressões",
    align: "right",
    type: "number",
    render: (value) => formatInteger(Number(value ?? 0)),
  },
  {
    key: "clicks",
    header: "Cliques",
    align: "right",
    type: "number",
    render: (value) => formatInteger(Number(value ?? 0)),
  },
  {
    key: "cpc",
    header: "CPC",
    align: "right",
    type: "currency",
    render: (value) => formatCurrency(Number(value ?? 0)),
  },
  {
    key: "cpm",
    header: "CPM",
    align: "right",
    type: "currency",
    render: (value) => formatCurrency(Number(value ?? 0)),
  },
  {
    key: "ctr",
    header: "CTR",
    align: "right",
    type: "percent",
    render: (value) => formatPercent(Number(value ?? 0)),
  },
];

/**
 * A coluna de Connect Rate existe **só** no bloco do Meta.
 *
 * Ele é `landing_page_view ÷ link_click`, duas ações que o Google Ads não tem, e o bloco
 * consolidado não pode misturar as duas bases: a rota publica `null` nos dois casos. Desenhar
 * a coluna ali daria uma coluna inteira de `N/D`. A reserva no `render` cobre o contrato
 * mudando de ideia sobre onde o campo é nulo — hoje ela não é alcançada.
 */
const COLUNAS_COM_CONNECT_RATE: TableColumn[] = [
  ...COLUNAS_COMUNS,
  {
    key: "connect_rate",
    header: "Connect Rate",
    align: "right",
    type: "percent",
    render: (value) =>
      value === null || value === undefined
        ? "N/D"
        : formatPercent(Number(value)),
  },
];

/** A descrição de cada bloco, por plataforma — as duas cores da etapa 1 do card. */
const DESCRICAO_DO_BLOCO: Readonly<Record<string, string>> = {
  meta: "A etapa 1 azul do funil, aberta por etapa de campanha",
  google: "A etapa 1 laranja do funil, aberta por etapa de campanha",
  all: "As três etapas somando as duas plataformas",
};

const DetalhamentoEtapa1Page: React.FC = () => {
  const { filter: filterState } = useGlobalFilter();
  const { selectedAccount } = useAccount();

  const startDate = useMemo(
    () => formatDateToISO(filterState.dateRange.startDate),
    [filterState.dateRange.startDate],
  );
  const endDate = useMemo(
    () => formatDateToISO(filterState.dateRange.endDate),
    [filterState.dateRange.endDate],
  );
  const comparison =
    filterState.comparison?.type === "previousYear"
      ? "previous_year"
      : "previous_period";

  const resolveCustomerUuid = useCallback(
    () =>
      selectedAccount?.customer_uuid ||
      localStorage.getItem("active_customer_uuid") ||
      "",
    [selectedAccount],
  );

  // Nasce carregando: com `false`, a primeira pintura mostraria o estado vazio antes de o
  // efeito rodar, e o leitor veria "sem conta conectada" numa loja que tem conta.
  const [loading, setLoading] = useState<boolean>(true);
  const [erro, setErro] = useState<boolean>(false);
  const [platforms, setPlatforms] = useState<GeneratedBlock[]>([]);
  const [combined, setCombined] = useState<GeneratedBlock | null>(null);

  useEffect(() => {
    // A resposta de um período que ficou para trás não pode sobrescrever a do período que
    // está na tela: trocar uma janela larga por uma estreita faz a estreita chegar primeiro.
    let atual = true;

    const carregar = async () => {
      const customerUuid = resolveCustomerUuid();
      if (!customerUuid) {
        setLoading(false);
        setErro(false);
        setPlatforms([]);
        setCombined(null);
        return;
      }

      setLoading(true);
      try {
        const { data } = await apiV1SalesFunnelStage1BreakdownGet({
          query: {
            customer_uuid: customerUuid,
            start_date: startDate,
            end_date: endDate,
            comparison,
          },
          throwOnError: true,
        });
        if (!atual) return;
        setErro(false);
        setPlatforms(data?.platforms ?? []);
        setCombined(data?.combined ?? null);
      } catch (err) {
        console.error("Erro ao carregar o detalhamento da etapa 1:", err);
        if (!atual) return;
        setErro(true);
        setPlatforms([]);
        setCombined(null);
      } finally {
        if (atual) setLoading(false);
      }
    };

    carregar();
    return () => {
      atual = false;
    };
  }, [startDate, endDate, comparison, resolveCustomerUuid]);

  const tabela = (bloco: GeneratedBlock) => (
    <StandardizedTable
      key={bloco.platform}
      title={bloco.platform_label.toUpperCase()}
      description={DESCRICAO_DO_BLOCO[bloco.platform] ?? ""}
      loading={loading}
      columns={
        bloco.platform === "meta" ? COLUNAS_COM_CONNECT_RATE : COLUNAS_COMUNS
      }
      data={bloco.stages as LinhaDaEtapa[]}
      // ⚠️ Do servidor, sempre — e nenhum rodapé quando ele não manda um: ver o cabeçalho.
      total={(bloco.total_row ?? undefined) as LinhaDaEtapa | undefined}
      hideTotals={bloco.total_row === null}
    />
  );

  const resumo = () => {
    if (loading) return <Skeleton className="h-[120px] w-full" />;
    if (erro) {
      return (
        <div className="flex items-center justify-center h-[120px] text-muted-foreground text-sm">
          Não foi possível carregar o detalhamento. Tente de novo em instantes.
        </div>
      );
    }
    if (combined === null) {
      return (
        <div className="flex items-center justify-center h-[120px] text-muted-foreground text-sm">
          Selecione uma loja para ver o detalhamento
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {combined.stages.map((etapa) => (
          <div
            key={etapa.stage}
            className="rounded-md bg-muted/30 border border-border px-5 py-4"
          >
            <div className="text-xs tracking-wider text-muted-foreground mb-2 font-medium">
              {etapa.stage_label}
            </div>
            <div className="text-[22px] font-medium">
              {formatCurrency(etapa.investment)}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              {formatPercent(etapa.investment_share)} do investimento ·{" "}
              {formatInteger(etapa.campaigns)}{" "}
              {etapa.campaigns === 1 ? "campanha" : "campanhas"}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6 page-background">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="page-title">DETALHAMENTO ETAPA 1</h1>
      </div>

      <GlobalFilter />

      {/* O consolidado primeiro: ele é a etapa 1 da Visão Geral, aberta em três faixas. */}
      <Card className="card-kpi-secondary">
        <CardHeader>
          <CardTitle className="text-center flex-1">
            INVESTIMENTO POR ETAPA DE FUNIL
          </CardTitle>
        </CardHeader>
        <CardContent>{resumo()}</CardContent>
      </Card>

      {combined !== null && tabela(combined)}
      {platforms.map((bloco) => tabela(bloco))}
    </div>
  );
};

export default DetalhamentoEtapa1Page;
